import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MousePointerClick, Sparkles, Type, Pencil, Brush, Layers, Download } from "lucide-react";

// Sticker badges — clickable jump-points into the portfolio.
// `target` is either an element ID or a CSS selector.
const stickers = [
  {
    id: "uiux",
    style: "circle",
    bg: "#0851F2",
    text: "#FFFFFF",
    title: "UI / UX",
    sub: "WEB · MOBILE · APPS",
    icon: <MousePointerClick className="w-5 h-5" strokeWidth={2.5} />,
    pos: "left-[4%] top-[8%]",
    rotate: -10,
    size: "w-[180px] h-[180px]",
    target: "#work",
  },
  {
    id: "graphic",
    style: "tag",
    bg: "#0a0a0a",
    text: "#FFFFFF",
    title: "GRAPHIC",
    sub: "POSTERS · CALENDARS",
    icon: <Layers className="w-3.5 h-3.5" strokeWidth={2.5} />,
    pos: "left-[18%] top-[58%]",
    rotate: -3,
    target: "#graphic",
  },
  {
    id: "logo",
    style: "circle",
    bg: "#FFFFFF",
    text: "#0a0a0a",
    title: "LOGO",
    sub: "★ MARK · WORDMARK ★",
    pos: "left-[34%] top-[18%]",
    rotate: 6,
    border: true,
    size: "w-[150px] h-[150px]",
    target: null,
  },
  {
    id: "illu",
    style: "tag",
    bg: "#A9CCFF",
    text: "#0a0a0a",
    title: "ILLUSTRATION",
    sub: "VECTOR · CHARACTER",
    icon: <Brush className="w-3.5 h-3.5" strokeWidth={2.5} />,
    pos: "left-[42%] top-[68%]",
    rotate: 4,
    target: "#illustrations",
  },
  {
    id: "brand",
    style: "tag",
    bg: "#0851F2",
    text: "#FFFFFF",
    title: "BRAND & IDENTITY",
    sub: "SYSTEMS · GUIDELINES",
    pos: "right-[14%] top-[16%]",
    rotate: -5,
    target: "#work",
  },
  {
    id: "typo",
    style: "tag",
    bg: "#FFFFFF",
    text: "#0a0a0a",
    title: "TYPOGRAPHY",
    sub: "BOOKS · EDITORIAL",
    icon: <Type className="w-3.5 h-3.5" strokeWidth={2.5} />,
    pos: "right-[6%] top-[44%]",
    rotate: -8,
    border: true,
    target: '[data-testid="typography-works"]',
  },
  {
    id: "paint",
    style: "circle",
    bg: "#0a0a0a",
    text: "#FFFFFF",
    title: "OIL\nPAINTING",
    sub: "★ ON CANVAS ★",
    icon: <Pencil className="w-4 h-4" strokeWidth={2.5} />,
    pos: "right-[28%] bottom-[10%]",
    rotate: 2,
    size: "w-[170px] h-[170px]",
    target: "#about",
  },
  {
    id: "magazine",
    style: "tag",
    bg: "#DDE7FF",
    text: "#0a0a0a",
    title: "MAGAZINES",
    sub: "LAYOUT · LONG-FORM",
    pos: "right-[3%] bottom-[18%]",
    rotate: 6,
    target: '[data-testid="magazine-flat-trigger"]',
  },
];

function Sticker({ s, onJump }) {
  const isClickable = Boolean(s.target);
  const handleClick = isClickable ? () => onJump(s.target) : undefined;
  const cursor = isClickable ? "cursor-pointer" : "cursor-default";
  const Wrapper = motion[isClickable ? "button" : "div"];
  const wrapperProps = isClickable
    ? { type: "button", onClick: handleClick, "aria-label": `Jump to ${s.title.replace(/\n/g, " ")}` }
    : { "aria-hidden": "true" };
  if (s.style === "circle") {
    return (
      <Wrapper
        {...wrapperProps}
        initial={{ scale: 0, rotate: s.rotate - 18 }}
        whileInView={{ scale: 1, rotate: s.rotate }}
        viewport={{ once: true }}
        transition={{ type: "spring", stiffness: 180, damping: 14 }}
        whileHover={isClickable ? { rotate: s.rotate + 5, scale: 1.06 } : undefined}
        whileTap={isClickable ? { scale: 0.96 } : undefined}
        className={`absolute ${s.pos} ${s.size} ${cursor} rounded-full flex flex-col items-center justify-center text-center px-4 shadow-[6px_8px_0_rgba(10,10,10,0.18)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0851F2]/40`}
        style={{
          background: s.bg,
          color: s.text,
          border: s.border ? "2px solid #0a0a0a" : "none",
        }}
        data-testid={`hero-sticker-${s.id}`}
      >
        {s.icon && <div className="mb-1.5 opacity-90">{s.icon}</div>}
        <div className="font-sans-display text-2xl md:text-[26px] leading-[0.9] whitespace-pre-line">
          {s.title}
        </div>
        <div className="mt-2 font-mono text-[9px] tracking-[0.18em]">
          {s.sub}
        </div>
      </Wrapper>
    );
  }
  return (
    <Wrapper
      {...wrapperProps}
      initial={{ scale: 0.6, opacity: 0, rotate: s.rotate - 10 }}
      whileInView={{ scale: 1, opacity: 1, rotate: s.rotate }}
      viewport={{ once: true }}
      transition={{ type: "spring", stiffness: 180, damping: 16 }}
      whileHover={isClickable ? { rotate: s.rotate + 4, scale: 1.06 } : undefined}
      whileTap={isClickable ? { scale: 0.96 } : undefined}
      className={`absolute ${s.pos} ${cursor} px-5 py-3 shadow-[6px_8px_0_rgba(10,10,10,0.18)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0851F2]/40`}
      style={{
        background: s.bg,
        color: s.text,
        borderRadius: "18px",
        border: s.border ? "2px solid #0a0a0a" : "none",
      }}
      data-testid={`hero-sticker-${s.id}`}
    >
      <div className="flex items-center gap-2">
        {s.icon}
        <div className="font-sans-display text-xl md:text-2xl leading-none">
          {s.title}
        </div>
      </div>
      <div className="mt-1.5 font-mono text-[9px] tracking-[0.18em]">
        {s.sub}
      </div>
    </Wrapper>
  );
}

// Decorative Klein-blue shapes — sit behind the stickers for added depth.
function BackdropShapes() {
  const KLEIN = "#0851F2";
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Big ring top-left */}
      <span
        className="absolute"
        style={{
          left: "-80px",
          top: "8%",
          width: "320px",
          height: "320px",
          borderRadius: "9999px",
          border: `2px solid ${KLEIN}`,
          opacity: 0.18,
        }}
      />
      {/* Solid blue dot center-left */}
      <span
        className="absolute"
        style={{
          left: "26%",
          top: "44%",
          width: "14px",
          height: "14px",
          borderRadius: "9999px",
          background: KLEIN,
        }}
      />
      {/* Plus sign mid */}
      <span
        className="absolute"
        style={{
          left: "48%",
          top: "12%",
          width: "28px",
          height: "28px",
          background:
            `linear-gradient(${KLEIN},${KLEIN}) center/100% 3px no-repeat,` +
            `linear-gradient(${KLEIN},${KLEIN}) center/3px 100% no-repeat`,
          opacity: 0.6,
        }}
      />
      {/* Tilted square right-top */}
      <span
        className="absolute"
        style={{
          right: "4%",
          top: "5%",
          width: "120px",
          height: "120px",
          background: KLEIN,
          opacity: 0.12,
          transform: "rotate(18deg)",
        }}
      />
      {/* Outline triangle bottom-left */}
      <svg
        className="absolute"
        style={{ left: "4%", bottom: "8%", width: "180px", height: "160px", opacity: 0.22 }}
        viewBox="0 0 180 160"
        fill="none"
      >
        <polygon
          points="90,10 170,150 10,150"
          stroke={KLEIN}
          strokeWidth="2.5"
        />
      </svg>
      {/* Concentric circle bottom-right */}
      <svg
        className="absolute"
        style={{ right: "8%", bottom: "4%", width: "220px", height: "220px", opacity: 0.2 }}
        viewBox="0 0 220 220"
        fill="none"
      >
        <circle cx="110" cy="110" r="100" stroke={KLEIN} strokeWidth="2" />
        <circle cx="110" cy="110" r="68" stroke={KLEIN} strokeWidth="2" />
        <circle cx="110" cy="110" r="32" stroke={KLEIN} strokeWidth="2" />
      </svg>
      {/* Wavy line top-right */}
      <svg
        className="absolute"
        style={{ right: "22%", top: "30%", width: "260px", height: "60px", opacity: 0.35 }}
        viewBox="0 0 260 60"
        fill="none"
      >
        <path
          d="M 5 30 Q 35 5, 65 30 T 125 30 T 185 30 T 250 30"
          stroke={KLEIN}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
      {/* Tiny dots cluster */}
      {[
        { l: "60%", t: "78%" },
        { l: "64%", t: "82%" },
        { l: "68%", t: "78%" },
      ].map((d, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: d.l,
            top: d.t,
            width: "8px",
            height: "8px",
            borderRadius: "9999px",
            background: KLEIN,
            opacity: 0.55,
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTarget = (target) => {
    if (!target) return;
    const el = target.startsWith("#")
      ? document.getElementById(target.slice(1))
      : document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="hero" className="relative" data-testid="hero-section">
      {/* ===== TOP — bold black panel, minimal text ===== */}
      <div className="relative bg-[#0a0a0a] text-white pt-32 md:pt-40 pb-12 md:pb-16 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
          {/* meta line */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex flex-wrap items-center gap-4 mb-8 md:mb-12"
          >
            <span className="w-2 h-2 rounded-full bg-[#0851F2] animate-pulse" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/65">
              Emilly Nešković
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">·</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/65">
              Belgrade
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">·</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/65">
              2026
            </span>
          </motion.div>

          {/* MASSIVE HEADLINE */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.08 }}
            className="font-sans-display uppercase leading-[0.85] text-[clamp(64px,13vw,220px)] text-white"
            data-testid="hero-headline"
          >
            UI/UX{" "}
            <span style={{ color: "#0851F2" }}>Designer</span>
            <span
              className="block"
              style={{
                color: "#0a0a0a",
                WebkitTextStroke: "2px #ffffff",
                textShadow: "0 0 1px rgba(255,255,255,0.25)",
              }}
            >
              & Graphic Designer
            </span>
          </motion.h1>

          {/* CTA row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end justify-between gap-6"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/45 max-w-[36ch]">
              ↓ A small studio of one — bold marks, considered layouts, the
              occasional sticker.
            </span>
            <div className="flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-8">
              <a
                href="https://customer-assets-39nsmqrw.emergentagent.net/job_portfolio-pro-1623/artifacts/58d781ca29a633cd_CV.pdf"
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="hero-cta-cv"
                className="group inline-flex items-center gap-3 px-5 py-3 border-2 border-white/40 text-white hover:bg-white hover:text-[#0a0a0a] hover:border-white transition-colors duration-300"
              >
                <Download className="w-4 h-4" strokeWidth={2.5} />
                <span className="font-mono text-[11px] uppercase tracking-[0.25em]">
                  Download CV
                </span>
              </a>
              <button
                type="button"
                onClick={() => scrollTo("contact")}
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-4 text-white border-b-2 border-white/40 pb-2 hover:border-[#0851F2] transition-colors duration-300"
              >
                <span className="font-sans-display uppercase text-2xl md:text-3xl">
                  Get in touch
                </span>
                <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ===== BOTTOM — sticker garden on grid (no portrait) ===== */}
      <div
        className="relative bg-[#F5F2EA] overflow-hidden"
        data-testid="hero-stage"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.08) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      >
        {/* Open-for-work pill on top */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, type: "spring" }}
          className="relative max-w-[1600px] mx-auto px-6 md:px-12 pt-12 md:pt-16 flex justify-center"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a0a0a] text-white rounded-full">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
              Open for new work · 2026
            </span>
          </span>
        </motion.div>

        {/* Sticker canvas */}
        <div
          className="relative max-w-[1600px] mx-auto px-4 md:px-10 lg:px-16 py-12 md:py-16 lg:py-20"
          style={{ minHeight: "min(78vh, 720px)" }}
        >
          {/* Decorative Klein-blue shapes — behind everything */}
          <BackdropShapes />

          {/* central "What I do" label */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center px-6"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#0a0a0a]/55">
                — What I do —
              </span>
              <h2 className="mt-4 font-sans-display uppercase text-[clamp(56px,9vw,140px)] leading-[0.85] text-[#0a0a0a]">
                A bit of <span style={{ color: "#0851F2" }}>everything</span>
              </h2>
              <p className="mt-6 max-w-[44ch] mx-auto text-base md:text-lg text-[#0a0a0a]/70 leading-relaxed">
                Interfaces, marks, magazines and the occasional canvas. Click
                any sticker to jump straight to that work.
              </p>
            </motion.div>
          </div>

          {/* Stickers */}
          {stickers.map((s) => (
            <Sticker key={s.id} s={s} onJump={scrollToTarget} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#0a0a0a]/65">
            ↓ Scroll, or jump straight in
          </span>
          <button
            type="button"
            onClick={() => scrollTo("work")}
            data-testid="hero-cta-work"
            className="group inline-flex items-center justify-between gap-10 px-7 py-5 bg-[#0a0a0a] text-white border-2 border-[#0a0a0a] hover:bg-[#0851F2] hover:border-[#0851F2] transition-colors duration-300"
          >
            <span className="font-mono text-xs uppercase tracking-[0.3em]">
              View selected work
            </span>
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
      </div>
    </section>
  );
}
